import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck, Home as HomeIcon, Building2, Route, MapPin, Phone, Mail,
  ArrowRight, ArrowUpRight, ShieldCheck, Clock, PackageCheck, Star,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMG = {
  hero: "https://images.pexels.com/photos/8962864/pexels-photo-8962864.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  residential: "https://images.pexels.com/photos/7464232/pexels-photo-7464232.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  commercial: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=80",
  movers: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1200&q=80",
  local: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80",
  t1: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NzM1ODQyOHww&ixlib=rb-4.1.0&q=85",
  t2: "https://images.pexels.com/photos/9623645/pexels-photo-9623645.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const EASE = [0.76, 0, 0.24, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ y: 40, opacity: 0 }}
    whileInView={{ y: 0, opacity: 1 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const inputCls =
  "w-full border border-zinc-300 rounded-none bg-white px-4 py-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0057FF] focus:ring-offset-0 placeholder:text-zinc-400";
const labelCls = "font-sub text-xs tracking-[0.2em] uppercase text-zinc-500 block mb-2";

const BtnPrimary = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`inline-flex items-center justify-center gap-2 bg-[#0057FF] text-white font-sub font-bold uppercase tracking-[0.15em] text-sm px-8 py-4 rounded-none shadow-[4px_4px_0px_0px_#09090B] transition-[transform,box-shadow] duration-150 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[7px_7px_0px_0px_#09090B] active:translate-x-0 active:translate-y-0 active:shadow-[2px_2px_0px_0px_#09090B] disabled:opacity-60 ${className}`}
  >
    {children}
  </button>
);

function Nav() {
  const links = [
    ["Services", "#services"],
    ["Process", "#process"],
    ["Reviews", "#reviews"],
    ["Contact", "#quote"],
  ];
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#F4F4F5]/95 backdrop-blur-md border-b border-zinc-300" data-testid="site-nav">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/logo192.png" alt="Anchor Moving logo" className="w-9 h-9 object-cover" data-testid="nav-logo-image" />
          <span className="font-display uppercase text-xl tracking-wide leading-none">Anchor Moving</span>
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {links.map(([label, href]) => (
            <a
              key={href}
              href={href}
              data-testid={`nav-link-${label.toLowerCase()}`}
              className="font-sub text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 hover:text-[#0057FF] transition-colors"
            >
              {label}
            </a>
          ))}
        </nav>
        <a href="#quote" data-testid="nav-quote-button">
          <BtnPrimary className="!px-5 !py-3 !text-xs">Get a Quote</BtnPrimary>
        </a>
      </div>
    </header>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex items-end overflow-hidden bg-zinc-900" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={IMG.hero} alt="Packed moving boxes ready for a Anchor Moving crew" className="w-full h-[120%] object-cover" data-testid="hero-bg-image" />
        <div className="absolute inset-0 bg-black/50" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-20 pt-40">
        <h1 className="font-display uppercase text-white leading-[0.85] tracking-tighter mt-6" data-testid="hero-heading">
          <MaskedLine delay={0.25} className="text-5xl sm:text-6xl lg:text-[clamp(3.75rem,7vw,6rem)]">We Move</MaskedLine>
          <MaskedLine delay={0.4} className="text-5xl sm:text-6xl lg:text-[clamp(3.75rem,7vw,6rem)]">So You Dont</MaskedLine>
          <MaskedLine delay={0.55} className="text-5xl sm:text-6xl lg:text-[clamp(3.75rem,7vw,6rem)]">
            <span className="text-[#0057FF]">Have Too</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.75}>
          <p className="mt-8 max-w-xl text-zinc-200 text-base md:text-lg leading-relaxed" data-testid="hero-subtext">
            Residential and commercial moves — across the street or across the country.
            Anchor crews pack it, haul it, and place it. You point, we lift.
          </p>
          <p className="mt-5 flex items-center gap-2 font-sub text-xs tracking-[0.2em] uppercase font-bold text-[#3B82F6] [text-shadow:0_1px_8px_rgba(0,0,0,0.9)]" data-testid="hero-location">
            <MapPin className="w-4 h-4" /> Stationed in the heart of Lebanon, Tennessee — providing service to the world
          </p>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1, ease: EASE }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a href="#booking" data-testid="hero-book-button">
            <BtnPrimary>Book Your Move <ArrowRight className="w-4 h-4" /></BtnPrimary>
          </a>
          <a href="#quote" data-testid="hero-quote-button">
            <button className="inline-flex items-center gap-2 border border-white/60 text-white font-sub font-bold uppercase tracking-[0.15em] text-sm px-8 py-4 rounded-none transition-colors duration-150 hover:bg-white hover:text-zinc-900">
              Free Estimate
            </button>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-16 max-w-lg border-t border-white/20 pt-6"
          data-testid="hero-stats"
        >
          <div>
            <p className="font-display text-2xl sm:text-3xl text-white">4.9★</p>
            <p className="font-sub text-[10px] tracking-[0.2em] uppercase text-zinc-400 mt-1">Avg Rating</p>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}

function Marquee() {
  const items = ["ANCHOR MOVING", "INDUSTRIAL STRENGTH", "RESIDENTIAL CARE", "LOCAL & LONG DISTANCE", "PACKED · HAULED · PLACED"];
  const row = [...items, ...items, ...items];
  return (
    <div className="bg-[#0057FF] border-y border-zinc-900 overflow-hidden py-4" data-testid="editorial-marquee">
      <div className="animate-marquee flex whitespace-nowrap w-max">
        {[0, 1].map((half) => (
          <div key={half} className="flex">
            {row.map((t, i) => (
              <span key={`${half}-${i}`} className="font-sub font-black uppercase tracking-[0.15em] text-white text-sm px-8 flex items-center gap-8">
                {t} <span className="w-2 h-2 bg-zinc-900 inline-block" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

const SERVICES = [
  { icon: HomeIcon, title: "Residential Moving", desc: "Apartments, townhouses, full family homes. Furniture wrapped, floors protected, every box accounted for.", img: IMG.residential, tag: "Homes" },
  { icon: Building2, title: "Commercial Moving", desc: "Office relocations with weekend crews and zero-downtime planning. Desks, servers, filing — back online Monday.", img: IMG.commercial, tag: "Business" },
  { icon: Route, title: "Long Distance", desc: "Interstate moves to anywhere in the United States — dedicated trucks, GPS-tracked loads, and guaranteed delivery windows.", img: IMG.movers, tag: "Interstate" },
  { icon: MapPin, title: "Local Moves", desc: "Same-city moves done in a day. Flat hourly crews, no fuel surcharges, no surprise fees.", img: IMG.local, tag: "Same City" },
];

function Services() {
  return (
    <section id="services" className="py-24 sm:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <p className="font-sub text-xs tracking-[0.3em] uppercase font-bold text-[#0057FF]">What We Haul</p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.9] tracking-tight mt-4 max-w-2xl">
            One Crew, <span className="text-[#0057FF]">One Purpose.</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-300 border border-zinc-300">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.08} className="bg-white group">
              <div className="overflow-hidden h-56 relative">
                <img
                  src={s.img}
                  alt={s.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  data-testid={`service-image-${s.tag.toLowerCase().replace(" ", "-")}`}
                />
                <span className="absolute top-4 left-4 bg-zinc-900 text-white font-sub text-[10px] tracking-[0.2em] uppercase px-3 py-1.5">{s.tag}</span>
              </div>
              <div className="p-8">
                <div className="flex items-center justify-between">
                  <s.icon className="w-6 h-6 text-[#0057FF]" strokeWidth={2} />
                  <span className="font-display text-zinc-200 text-4xl group-hover:text-[#0057FF] transition-colors">0{i + 1}</span>
                </div>
                <h3 className="font-display uppercase text-2xl mt-4">{s.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed mt-3">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { num: "01", title: "Get Your Quote", desc: "Tell us what you're moving and where it's going. Flat, honest pricing back within one business hour.", icon: PackageCheck },
  { num: "02", title: "Lock In Your Slot", desc: "Pick your move date and arrival window online. Your crew is reserved the moment you book.", icon: Clock },
  { num: "03", title: "We Pack & Haul", desc: "Uniformed crew arrives on time. Everything wrapped, strapped, and stacked like Tetris champions.", icon: Truck },
  { num: "04", title: "You Settle In", desc: "We place every piece where you want it and haul the empty boxes away. Walkthrough before we leave.", icon: ShieldCheck },
];

function Process() {
  return (
    <section id="process" className="py-24 sm:py-32 bg-zinc-900 text-white" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <p className="font-sub text-xs tracking-[0.3em] uppercase font-bold text-[#0057FF]">The Anchor Method</p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.9] tracking-tight mt-4">
            A Move Is A Mission. <span className="text-[#0057FF]">We Run It In Four Chapters.</span>
          </h2>
        </Reveal>
        <div className="mt-20 space-y-0 border-t border-zinc-700">
          {STEPS.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.06}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-12 border-b border-zinc-700 group" data-testid={`process-step-${s.num}`}>
                <div className="md:col-span-3">
                  <span className="font-display text-6xl sm:text-7xl text-zinc-700 group-hover:text-[#0057FF] transition-colors duration-300">{s.num}</span>
                </div>
                <div className="md:col-span-4 flex items-start gap-4">
                  <s.icon className="w-6 h-6 text-[#0057FF] mt-1 shrink-0" strokeWidth={2} />
                  <h3 className="font-display uppercase text-2xl sm:text-3xl leading-tight">{s.title}</h3>
                </div>
                <div className="md:col-span-5">
                  <p className="text-zinc-400 text-base leading-relaxed">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const TESTIMONIALS = [
  { name: "Rachel Donovan", role: "3-Bedroom Home, Cross-Town", quote: "The crew wrapped my grandmother's piano like it was museum cargo. Not a scratch on anything — and they finished two hours early.", img: IMG.t1 },
  { name: "Marcus Webb", role: "Office Relocation, 40 Desks", quote: "Anchor moved our whole floor over a weekend. Monday morning the team sat down and logged in like nothing happened. Flawless.", img: IMG.t2 },
];

function Testimonials() {
  return (
    <section id="reviews" className="py-24 sm:py-32" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <p className="font-sub text-xs tracking-[0.3em] uppercase font-bold text-[#0057FF]">Word On The Street</p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.9] tracking-tight mt-4">
            Customers Talk. <span className="text-[#0057FF]">We Listen.</span>
          </h2>
        </Reveal>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          {TESTIMONIALS.map((t, i) => (            <Reveal key={t.name} delay={i * 0.1}>
              <figure className="bg-white border border-zinc-300 p-10 shadow-[4px_4px_0px_0px_#09090B] hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[8px_8px_0px_0px_#09090B] transition-[transform,box-shadow] duration-200" data-testid={`testimonial-card-${i}`}>
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-[#0057FF] text-[#0057FF]" />
                  ))}
                </div>
                <blockquote className="text-lg leading-relaxed text-zinc-800">“{t.quote}”</blockquote>
                <figcaption className="mt-8 flex items-center gap-4">
                  <img src={t.img} alt={t.name} className="w-12 h-12 object-cover border border-zinc-300" />
                  <div>
                    <p className="font-sub font-bold uppercase text-sm tracking-wide">{t.name}</p>
                    <p className="text-xs text-zinc-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15} className="mt-12 text-center">
          <a
            href="https://g.page/r/CY6-6GQhYxQlEAE/review"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-review-button"
          >
            <BtnPrimary>
              <Star className="w-4 h-4 fill-white" /> Review Us On Google
            </BtnPrimary>
          </a>
          <p className="mt-4 text-xs text-zinc-500 font-sub uppercase tracking-[0.15em]">Moved with us before? 60 seconds helps the next family find us.</p>
        </Reveal>
      </div>
    </section>
  );
}

const MOVE_TYPES = ["Residential", "Commercial", "Long Distance", "Local"];
const TIME_SLOTS = ["8:00 AM", "10:00 AM", "12:00 PM", "2:00 PM", "4:00 PM"];
const HOME_SIZES = ["Studio / 1BR", "2 Bedroom", "3 Bedroom", "4+ Bedroom", "Office"];

function QuoteForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", move_type: "Residential", from_address: "", to_address: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received. We'll call you within one business hour.");
      setForm({ name: "", email: "", phone: "", move_type: "Residential", from_address: "", to_address: "", message: "" });
    } catch {
      toast.error("Something went wrong sending your request. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" className="py-24 sm:py-32 border-t border-zinc-300" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <p className="font-sub text-xs tracking-[0.3em] uppercase font-bold text-[#0057FF]">Free Estimate</p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.9] tracking-tight mt-4">
            Get A <span className="text-[#0057FF]">Same Day Quote</span>
          </h2>
          <p className="mt-6 text-zinc-600 text-base leading-relaxed max-w-md">
            No obligations, no pushy sales calls. One flat number for your whole move — labor, truck, fuel, and wrap included.
          </p>
          <div className="mt-10 space-y-4">
            <a href="tel:+12702255228" data-testid="quote-phone-link" className="flex items-center gap-4 group w-fit">
              <span className="w-11 h-11 border border-zinc-300 bg-white flex items-center justify-center group-hover:bg-[#0057FF] transition-colors">
                <Phone className="w-4 h-4 text-zinc-900 group-hover:text-white transition-colors" strokeWidth={2} />
              </span>
              <span className="font-sub font-bold text-lg tracking-wide">270-225-5228</span>
            </a>
            <a href="mailto:615.anchormoving@gmail.com" data-testid="quote-email-link" className="flex items-center gap-4 group w-fit">
              <span className="w-11 h-11 border border-zinc-300 bg-white flex items-center justify-center group-hover:bg-[#0057FF] transition-colors">
                <Mail className="w-4 h-4 text-zinc-900 group-hover:text-white transition-colors" strokeWidth={2} />
              </span>
              <span className="font-sub font-bold text-lg tracking-wide">615.anchormoving@gmail.com</span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} className="bg-white border border-zinc-300 shadow-[8px_8px_0px_0px_#09090B] p-8 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="quote-form">
            <div>
              <label className={labelCls}>Full Name</label>
              <input data-testid="quote-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Carter" />
            </div>
            <div>
              <label className={labelCls}>Phone</label>
              <input data-testid="quote-phone-input" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(555) 000-0000" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Email</label>
              <input data-testid="quote-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@email.com" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Move Type</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-zinc-300 border border-zinc-300">
                {MOVE_TYPES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`quote-type-${t.toLowerCase().replace(" ", "-")}`}
                    onClick={() => setForm((f) => ({ ...f, move_type: t }))}
                    className={`px-3 py-3 text-xs font-sub font-bold uppercase tracking-wider transition-colors rounded-none ${form.move_type === t ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:text-[#0057FF]"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls}>Moving From</label>
              <input data-testid="quote-from-input" required value={form.from_address} onChange={set("from_address")} className={inputCls} placeholder="Street, City" />
            </div>
            <div>
              <label className={labelCls}>Moving To</label>
              <input data-testid="quote-to-input" required value={form.to_address} onChange={set("to_address")} className={inputCls} placeholder="Street, City" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Anything We Should Know? (Optional)</label>
              <textarea data-testid="quote-message-input" rows={3} value={form.message} onChange={set("message")} className={inputCls} placeholder="Piano on the second floor, narrow driveway…" />
            </div>
            <div className="sm:col-span-2">
              <BtnPrimary data-testid="quote-submit-button" type="submit" disabled={sending} className="w-full">
                {sending ? "Sending…" : "Send Quote Request"} <ArrowUpRight className="w-4 h-4" />
              </BtnPrimary>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function BookingForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", move_date: "", time_slot: "",
    move_type: "Residential", home_size: "2 Bedroom", from_address: "", to_address: "", notes: "",
  });
  const [sending, setSending] = useState(false);
  const [takenSlots, setTakenSlots] = useState([]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (!form.move_date) {
      setTakenSlots([]);
      return;
    }
    axios.get(`${API}/bookings/slots`, { params: { date: form.move_date } })
      .then((res) => {
        setTakenSlots(res.data.taken || []);
        setForm((f) => (res.data.taken || []).includes(f.time_slot) ? { ...f, time_slot: "" } : f);
      })
      .catch(() => setTakenSlots([]));
  }, [form.move_date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.time_slot) {
      toast.error("Pick an arrival time slot for your crew.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success(`Booked! Your crew arrives ${form.move_date} at ${form.time_slot}. Confirmation details are on the way.`);
      setForm({ name: "", email: "", phone: "", move_date: "", time_slot: "", move_type: "Residential", home_size: "2 Bedroom", from_address: "", to_address: "", notes: "" });
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("That arrival window was just taken. Pick another slot.");
        const res = await axios.get(`${API}/bookings/slots`, { params: { date: form.move_date } }).catch(() => null);
        if (res) setTakenSlots(res.data.taken || []);
        setForm((f) => ({ ...f, time_slot: "" }));
      } else {
        toast.error("Booking failed. Please call 270-225-5228 and we'll lock it in by phone.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="booking" className="py-24 sm:py-32 bg-zinc-900 text-white border-t border-zinc-800" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal>
          <p className="font-sub text-xs tracking-[0.3em] uppercase font-bold text-[#0057FF]">Dispatch Board</p>
          <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[0.9] tracking-tight mt-4 max-w-3xl">
            Pick A Date. Pick A Slot. <span className="text-[#0057FF]">Your Crew Is Locked In.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="mt-14 bg-zinc-100 text-zinc-900 border border-zinc-700 shadow-[8px_8px_0px_0px_#0057FF] p-8 sm:p-10" data-testid="booking-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label className={labelCls}>Full Name</label>
                <input data-testid="booking-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Carter" />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input data-testid="booking-phone-input" required value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(555) 000-0000" />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input data-testid="booking-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@email.com" />
              </div>
              <div>
                <label className={labelCls}>Move Date</label>
                <input data-testid="booking-date-input" required type="date" min={today} value={form.move_date} onChange={set("move_date")} className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Arrival Window</label>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-px bg-zinc-300 border border-zinc-300">
                  {TIME_SLOTS.map((slot) => {
                    const taken = takenSlots.includes(slot);
                    return (
                      <button
                        key={slot}
                        type="button"
                        disabled={taken}
                        data-testid={`booking-slot-${slot.replace(/[: ]/g, "-").toLowerCase()}`}
                        onClick={() => setForm((f) => ({ ...f, time_slot: slot }))}
                        className={`px-3 py-3.5 text-xs font-sub font-bold uppercase tracking-wider transition-colors rounded-none ${taken ? "bg-zinc-200 text-zinc-400 line-through cursor-not-allowed" : form.time_slot === slot ? "bg-[#0057FF] text-white" : "bg-white text-zinc-600 hover:text-[#0057FF]"}`}
                      >
                        {slot}{taken ? " · Booked" : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <label className={labelCls}>Move Type</label>
                <select data-testid="booking-type-select" value={form.move_type} onChange={set("move_type")} className={inputCls}>
                  {MOVE_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Home / Office Size</label>
                <select data-testid="booking-size-select" value={form.home_size} onChange={set("home_size")} className={inputCls}>
                  {HOME_SIZES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Moving From</label>
                <input data-testid="booking-from-input" required value={form.from_address} onChange={set("from_address")} className={inputCls} placeholder="Street, City" />
              </div>
              <div>
                <label className={labelCls}>Moving To</label>
                <input data-testid="booking-to-input" required value={form.to_address} onChange={set("to_address")} className={inputCls} placeholder="Street, City" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Notes For The Crew (Optional)</label>
                <textarea data-testid="booking-notes-input" rows={2} value={form.notes} onChange={set("notes")} className={inputCls} placeholder="Gate code, elevator booking, heavy items…" />
              </div>
            </div>
            <BtnPrimary data-testid="booking-submit-button" type="submit" disabled={sending} className="w-full mt-8">
              {sending ? "Locking In Your Crew…" : "Book My Move"} <Truck className="w-4 h-4" />
            </BtnPrimary>
            <p className="text-center text-xs text-zinc-500 mt-4 font-sub uppercase tracking-[0.15em]">Free reschedule up to 48h before your move</p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-zinc-950 text-white pt-24 overflow-hidden" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 pb-20 border-b border-zinc-800">
          <div>
            <div className="flex items-center gap-3">
              <img src="/logo192.png" alt="Anchor Moving logo" className="w-10 h-10 object-cover" data-testid="footer-logo-image" />
              <span className="font-display uppercase text-2xl">Anchor Moving</span>
            </div>
            <p className="mt-6 text-zinc-400 text-sm leading-relaxed max-w-xs">
              Industrial strength, residential care. Local and interstate moves, done right.
            </p>
          </div>
          <div>
            <p className="font-sub text-xs tracking-[0.3em] uppercase font-bold text-[#0057FF] mb-6">Contact Dispatch</p>
            <div className="space-y-3 text-sm text-zinc-300">
              <a href="tel:+12702255228" data-testid="footer-phone-link" className="block hover:text-[#0057FF] transition-colors">270-225-5228</a>
              <a href="mailto:615.anchormoving@gmail.com" data-testid="footer-email-link" className="block hover:text-[#0057FF] transition-colors">615.anchormoving@gmail.com</a>
              <p className="text-zinc-500">Sun–Sat · 8AM–6PM</p>
              <p className="text-zinc-500 flex items-center gap-2" data-testid="footer-location"><MapPin className="w-4 h-4 text-[#0057FF]" /> Lebanon, Tennessee — serving everywhere</p>
            </div>
          </div>
          <div>
            <p className="font-sub text-xs tracking-[0.3em] uppercase font-bold text-[#0057FF] mb-6">Quick Moves</p>
            <div className="space-y-3 text-sm">
              <a href="#quote" data-testid="footer-quote-link" className="block text-zinc-300 hover:text-[#0057FF] transition-colors">Get a free quote</a>
              <a href="#booking" data-testid="footer-booking-link" className="block text-zinc-300 hover:text-[#0057FF] transition-colors">Book a move date</a>
              <Link to="/long-distance-movers" data-testid="footer-long-distance-link" className="block text-zinc-300 hover:text-[#0057FF] transition-colors">Long distance movers</Link>
              <Link to="/service-areas" data-testid="footer-areas-link" className="block text-zinc-300 hover:text-[#0057FF] transition-colors">Service areas</Link>
              <a href="https://g.page/r/CY6-6GQhYxQlEAE/review" target="_blank" rel="noopener noreferrer" data-testid="footer-review-link" className="block text-zinc-300 hover:text-[#0057FF] transition-colors">Leave a Google review</a>
              <Link to="/admin/login" data-testid="footer-admin-link" className="block text-zinc-300 hover:text-[#0057FF] transition-colors">Crew login</Link>
            </div>
          </div>
          <div data-testid="footer-restrictions">
            <p className="font-sub text-xs tracking-[0.3em] uppercase font-bold text-[#0057FF] mb-6">What We Don&rsquo;t Do</p>
            <p className="text-sm text-zinc-400 leading-relaxed">
              Anchor Moving will NOT transport flammable liquids, firearms, ammunition, plant life,
              perishables, or jewelry — and we ask that any lawn mowers or motorcycles be drained
              of fuel before loading occurs.
            </p>
          </div>
        </div>
      </div>
      <div className="px-2 select-none" aria-hidden="true">
        <p className="font-display uppercase text-[13vw] leading-[0.8] tracking-tighter text-zinc-800 whitespace-nowrap text-center translate-y-[12%]">
          Anchor Moving
        </p>
      </div>
    </footer>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <div className="noise-overlay" />
      <Nav />
      <Hero />
      <Marquee />
      <Services />
      <Process />
      <Testimonials />
      <QuoteForm />
      <BookingForm />
      <Footer />
    </div>
  );
}
